import _ from 'lodash';
import { action, computed, makeObservable, observable, runInAction } from 'mobx';
import { AgenturModel } from 'app/models/agentur.model';
import { AgenturStore } from '../agentur.store';
import { AgtAkumaPrimeModel, AgtAkumaPrimeStore } from '../agt.akumaprime.store';
import colors from 'assets/scss/colors.module.scss';
import { ISelectOpt } from 'app/components/common/Select';
import { formatKw, getFirstWeekOfCurrentYear } from 'app/utils';
import { BaseUiStore } from './base.ui.store';
import moment from 'moment';

export enum AkumaPrimeType {
    einordnung = 'einordnung',
    terminierung = 'terminierung',
    beziehung = 'beziehung',
    // terminierungErgebnis = 'terminierungErgebnis',
    einordnungsquote = 'einordnungsquote',
}
export interface IAkumaPrimeProps {
    einordnungSach?: number;
    einordnungLeben?: number;
    einordnungKranken?: number;
    einordnungSonstige?: number;
    einordnungFonds?: number;
    einordnungNichtKontaktieren?: number;
    einordnungNichtEingeordnet?: number;
    einordnungBestehendeAktion?: number;
    beziehungUnbekannt?: number;
    beziehungWenigerGut?: number;
    beziehungMittel?: number;
    beziehungGut?: number;
    terminierungAgentur?: number;
    terminierungExternTotal?: number;
}

export class AgtAkumaPrimeKwVM implements IAkumaPrimeProps {
    agt: AgenturModel;
    hasData: boolean;
    kw: number;

    // Einordnung properties
    einordnungTotal: number;
    eingeordnetTotal: number;
    einordnungsQuote: number;
    einordnungSach: number;
    einordnungLeben: number;
    einordnungKranken: number;
    einordnungSonstige: number;
    einordnungFonds: number;
    einordnungNichtKontaktieren: number;
    einordnungBestehendeAktion: number;
    einordnungNichtEingeordnet: number;

    // Abwahl properties
    abwahl: number;
    abwahlQuote: number;

    // Terminierung properties
    zuKontaktierendeKunden: number;
    terminierungAgentur: number;
    terminierungExternTotal: number;
    terminierungExternQuote: number;

    // Beziehung properties
    beziehungUnbekannt: number;
    beziehungWenigerGut: number;
    beziehungMittel: number;
    beziehungGut: number;
    kundenkontaktquote: number;

    maxKw: number;

    constructor(agt: AgenturModel, item?: AgtAkumaPrimeModel) {
        makeObservable(this);
        this.agt = agt;
        this.hasData = !!item;

        this.kw = item?.kw ?? 0;
        this.einordnungTotal = item?.einordnungTotal ?? 0;
        this.eingeordnetTotal = item?.eingeordnetTotal ?? 0;
        this.einordnungsQuote = item?.einordnungsquote ?? 0;
        this.einordnungSach = item?.einordnungSach ?? 0;
        this.einordnungLeben = item?.einordnungLeben ?? 0;
        this.einordnungKranken = item?.einordnungKranken ?? 0;
        this.einordnungSonstige = item?.einordnungSonstige ?? 0;
        this.einordnungFonds = item?.einordnungFonds ?? 0;
        this.einordnungNichtKontaktieren = item?.einordnungNichtKontaktieren ?? 0;
        this.einordnungBestehendeAktion = item?.einordnungBestehendeAktion ?? 0;
        this.einordnungNichtEingeordnet = item?.einordnungNichtEingeordnet ?? 0;
        this.abwahl = item?.abwahl ?? 0;
        this.abwahlQuote = item?.abwahlquote ?? 0;
        this.zuKontaktierendeKunden = item?.zuKontaktierendeKunden ?? 0;
        this.terminierungAgentur = item?.terminierungAgentur ?? 0;
        this.terminierungExternTotal = item?.terminierungExternTotal ?? 0;
        this.terminierungExternQuote = item?.terminierungExternQuote ?? 0;
        this.beziehungUnbekannt = item?.beziehungUnbekannt ?? 0;
        this.beziehungWenigerGut = item?.beziehungWenigerGut ?? 0;
        this.beziehungMittel = item?.beziehungMittel ?? 0;
        this.beziehungGut = item?.beziehungGut ?? 0;
        this.kundenkontaktquote = item?.kundenkontaktquote ?? 0;
        this.maxKw = item?.maxKw ?? 0;
    }

    @computed
    get agtId() {
        return this.agt.agtId;
    }

    @computed
    get bm() {
        return this.kw;
    }

    // Helper method to get values for charts
    @computed
    get akumePrimeVals(): IAkumaPrimeVal[] {
        if (!this.hasData) return [];
        const vals: IAkumaPrimeVal[] = [];
        AkumaPrimeSchema.forEach((s) => {
            let value = 0;
            switch (s.path) {
                case 'einordnungSach': value = this.einordnungSach; break;
                case 'einordnungLeben': value = this.einordnungLeben; break;
                case 'einordnungKranken': value = this.einordnungKranken; break;
                case 'einordnungSonstige': value = this.einordnungSonstige; break;
                case 'einordnungFonds': value = this.einordnungFonds; break;
                case 'einordnungNichtKontaktieren': value = this.einordnungNichtKontaktieren; break;
                case 'einordnungNichtEingeordnet': value = this.einordnungNichtEingeordnet; break;
                case 'einordnungBestehendeAktion': value = this.einordnungBestehendeAktion; break;
                case 'beziehungUnbekannt': value = this.beziehungUnbekannt; break;
                case 'beziehungWenigerGut': value = this.beziehungWenigerGut; break;
                case 'beziehungMittel': value = this.beziehungMittel; break;
                case 'beziehungGut': value = this.beziehungGut; break;
                case 'zuKontaktierendeKunden': value = this.zuKontaktierendeKunden; break;
                case 'terminierungAgentur': value = this.terminierungAgentur; break;
                case 'terminierungAgentur': value = this.terminierungAgentur; break;
                case 'kundenkontaktquote': value = this.kundenkontaktquote; break;
            }
            vals.push({ ...s, value, percent: 0, groupTotal: 0 });
        });
        return vals;
    }
}

// ******************************************************************************************

export interface IAkumaPrimeDef {
    label: string;
    sort: number;
    key: string;
    path: string;
    type: AkumaPrimeType;
    color: string;
}

// Einordnung
// Rang	ID	Label
// 1	S	Sach
// 2	L	Leben
// 3	K	Kranken
// 4	SO	Sonstige
// 5	NK	Nicht Kontaktieren
// 6	NE	Nicht eingeordnet
// 7	BA	Bestehende Aktion

// Terminierung
// Rang	ID	Label
// 1	T	Termin
// 2	KI	Kein Interesse
// 3	KNE	Kunde nicht erreicht
// 4	KNK	Kunde nicht kontaktiert

// Beziehung
// Rang	ID	Label
// 1	BZU	Beziehung: unbekannt/keine info
// 2	BZWG Beziehung: weniger gut
// 3	BZM	Beziehung: mittel
// 4	BZG	Beziehung: gut
// 5	BZD	Beziehung: Default

// Zentrale Nachbearbeitung
// Rang	ID	Label
// 1	1	Nachbeiarbeitet
// 2	0	Nicht nachbearbeitet

export const AkumaPrimeSchema: IAkumaPrimeDef[] = [
    // Einordnung
    {
        label: 'Sach',
        sort: 1,
        key: 'S',
        type: AkumaPrimeType.einordnung,
        path: 'einordnungSach',
        color: colors.yellow,
    },
    {
        label: 'Leben',
        sort: 2,
        key: 'L',
        type: AkumaPrimeType.einordnung,
        path: 'einordnungLeben',
        color: colors.blue,
    },
    {
        label: 'Kranken',
        sort: 3,
        key: 'K',
        type: AkumaPrimeType.einordnung,
        path: 'einordnungKranken',
        color: colors.green,
    },
    {
        label: 'Sonstige',
        sort: 4,
        key: 'SO',
        type: AkumaPrimeType.einordnung,
        path: 'einordnungSonstige',
        color: colors.orange,
    },
    {
        label: 'Fonds',
        sort: 5,
        key: 'F',
        type: AkumaPrimeType.einordnung,
        path: 'einordnungFonds',
        color: colors.richTurquoise,
    },
    {
        label: 'Nicht Kontaktieren',
        sort: 6,
        key: 'NK',
        type: AkumaPrimeType.einordnung,
        path: 'einordnungNichtKontaktieren',
        color: colors.richRed,
    },
    {
        label: 'Bestehende Aktion',
        sort: 7,
        key: 'BA',
        type: AkumaPrimeType.einordnung,
        path: 'einordnungBestehendeAktion',
        color: colors.purple,
    },
    {
        label: 'Nicht eingeordnet',
        sort: 8,
        key: 'NE',
        type: AkumaPrimeType.einordnung,
        path: 'einordnungNichtEingeordnet',
        color: colors.greyLight,
    },

    // Beziehung
    {
        label: 'Beziehung: unbekannt/keine info',
        sort: 1,
        key: 'BZU',
        type: AkumaPrimeType.beziehung,
        path: 'beziehungUnbekannt',
        color: colors.yellow,
    },
    {
        label: 'Beziehung: weniger gut',
        sort: 2,
        key: 'BZWG',
        type: AkumaPrimeType.beziehung,
        path: 'beziehungWenigerGut',
        color: colors.richRed,
    },
    {
        label: 'Beziehung: mittel',
        sort: 3,
        key: 'BZM',
        type: AkumaPrimeType.beziehung,
        path: 'beziehungMittel',
        color: colors.richBlue,
    },
    {
        label: 'Beziehung: gut',
        sort: 4,
        key: 'BZG',
        type: AkumaPrimeType.beziehung,
        path: 'beziehungGut',
        color: colors.green,
    },

    // Terminierung intern/extern
    // ziel: this.prime.zuKontaktierendeKunden,
    // ergebnis: this.prime.terminierungExternTotal,
    {
        label: 'Zu kontaktieren',
        sort: 1,
        key: 'ZK',
        type: AkumaPrimeType.terminierung,
        path: 'zuKontaktierendeKunden',
        color: colors.purple,
    },
    {
        label: 'Terminierung durch Agentur',
        sort: 1,
        key: 'AGT',
        type: AkumaPrimeType.terminierung,
        path: 'terminierungAgentur',
        color: colors.darkYellow,
    },
    // {
    // 	label: 'Externe Terminierung',
    // 	sort: 2,
    // 	key: 'CC',
    // 	type: AkumaPrimeType.terminierung,
    // 	path: 'terminierungExtern',
    // 	color: colors.richTurquoise,
    // },
    {
        label: 'Externe Terminierung',
        sort: 2,
        key: 'CC',
        type: AkumaPrimeType.terminierung,
        path: 'terminierungExternTotal',
        color: colors.richTurquoise,
    },

];

// Simplified value interface for charts - no longer a class
export interface IAkumaPrimeVal extends IAkumaPrimeDef {
    value: number;
    percent: number;
    groupTotal: number;
}

export class AgtAkumaPrimeUiStore extends BaseUiStore<AgtAkumaPrimeKwVM> {
    name = 'AgtAkumaPrimeUiStore';
    agenturStore: AgenturStore;
    agtAkumaPrimeStore: AgtAkumaPrimeStore;

    constructor(agenturStore: AgenturStore, agtAkumaPrimeStore: AgtAkumaPrimeStore) {
        super();
        makeObservable(this);
        this.agtAkumaPrimeStore = agtAkumaPrimeStore;
        this.agenturStore = agenturStore;
    }
    onLogout() {
        this.agtAkumaPrimeStore.clear();
        this.agenturStore.onLogout();
    }

    filterByAgtId(agtId: number) {
        return this.items.filter((i) => i.agtId === agtId);
    }

    filterByAgtIdandKw(agtId: number) {
        let items = this.items.filter((i) => i.agtId === agtId);
        items = items.filter((i) => this.currentKWs.includes(i.kw));
        return items;
    }

    @observable
    allVmItems: AgtAkumaPrimeKwVM[] = [];

    @observable
    showGraphs: boolean = false;

    @observable
    kws: number[] = [];

    @computed
    get kwsOpts(): ISelectOpt[] {
        return this.kws.map((kw) => {
            return {
                value: kw,
                label: formatKw(kw),
            };
        });
    }

    @computed
    get minKw(): number {
        if (this.minKwCurrent) {
            return this.minKwCurrent;
        }
        if (this.kws.length) {
            return this.kws[this.kws.length - 5];
        }
        return 0;
    }

    @computed
    get maxKw(): number {
        if (this.maxKwCurrent) {
            return this.maxKwCurrent;
        }
        if (this.kws.length) {
            return this.kws[this.kws.length - 1];
        }
        return 0;
    }

    @computed
    get firstWeek() {
        return this.kws[0];
    }
    @computed
    get lastWeek() {
        return this.kws[this.kws.length - 2];
    }

    @observable
    minKwCurrent?: number;

    @observable
    maxKwCurrent?: number;

    @observable
    chartKey = 'charty';

    @computed
    get currentKWs() {
        const res: number[] = [];
        this.kws.forEach((kw) => {
            if (this.minKw && kw >= this.minKw) {
                if (this.maxKw && kw <= this.maxKw) {
                    res.push(kw);
                }
            }
            if (!this.minKw) {
                // this case is needed for the go live for the first 4 weeks
                if (this.maxKw && kw <= this.maxKw) {
                    res.push(kw);
                }
            }
        });
        return res;
    }

    @computed
    get currentKWItems() {
        // Items are already aggregated for the selected KW range
        return this.items;
    }


    checkAgtParticipation(agtId: number) {
        const agtItem = this.filterByAgtId(agtId);
        // filter by lastWeek
        const participatedLastWeek = agtItem.filter((i) => i.kw === this.lastWeek);
        return participatedLastWeek.length > 0;
    }

    @action
    async _baseLoad() {
        if (this.agenturStore.session.isAdmin) {
            this.showGraphs = false;
        } else {
            this.showGraphs = true;
        }

        const promises = await Promise.all([this.agtAkumaPrimeStore.getDistinctKWs(), this.agenturStore.findAll()]);
        const kws = promises[0];
        let agts = promises[1].slice();

        runInAction(() => {
            this.kws = kws;
            const firstWeekOfYear = getFirstWeekOfCurrentYear();
            const kwsInCurrentYear = this.kws.filter((kw) => kw >= firstWeekOfYear);
            this.minKwCurrent = kwsInCurrentYear.length >= 3 ? firstWeekOfYear : _.min(this.kws);

            this.maxKwCurrent = _.max(this.kws);
        });

        // Create base VM items for all agencies
        const res: AgtAkumaPrimeKwVM[] = [];
        agts.forEach((agt) => {
            res.push(new AgtAkumaPrimeKwVM(agt));
        });

        runInAction(() => {
            this.items = res;
            this.allVmItems = res;
            if (kws.length > 0) {
                this.datenStand = this.maxKwCurrent;
            }
        });
    }

    @action
    async aggreagateAgtsByKw(minKw?: number, maxKw?: number) {
        // load() first, so minKwCurrent/maxKwCurrent can serve as defaults. Without them an
        // undefined range queries ALL weeks and races against the filtered follow-up query.
        await this.load();
        minKw = minKw ?? this.minKwCurrent;
        maxKw = maxKw ?? this.maxKwCurrent;

        // Create a cache key based on the parameters
        const cacheKey = `aggreagateAgtsByKw_${minKw || 'null'}_${maxKw || 'null'}`;

        // Wrap the function with caching to prevent duplicate calls
        return this.withCache(
            cacheKey,
            async () => {
                let primes = await this.agtAkumaPrimeStore.aggregateByAgt(minKw, maxKw);

                // Group primes by vtnrId in a single pass
                const primesByVtnrId = new Map<number, AgtAkumaPrimeModel>();
                primes.forEach((prime) => {
                    primesByVtnrId.set(prime.vtnrId, prime);
                });

                // Build results using direct Map lookup and filter in one pass
                const res: AgtAkumaPrimeKwVM[] = this.allVmItems
                    .map((vm) => {
                        const p = primesByVtnrId.get(vm.agt.agtId);
                        return new AgtAkumaPrimeKwVM(vm.agt, p);
                    })
                    .filter((r) => r.hasData);

                runInAction(() => {
                    this.items = res;
                });
                return this.items;
            },
            {
                cacheResult: true,
                measureTime: true,
            }
        );
    }

    @action
    async aggregateAgtByKw(agtId: number, minKw?: number, maxKw?: number) {
        await this.load();
        minKw = minKw ?? this.minKwCurrent;
        maxKw = maxKw ?? this.maxKwCurrent;

        // Create a cache key based on the parameters
        const cacheKey = `aggregateAgtByKw_${agtId}_${minKw || 'null'}_${maxKw || 'null'}`;

        return this.withCache(
            cacheKey,
            async () => {
                let primes = await this.agtAkumaPrimeStore.aggregateAgtByKw(agtId, minKw, maxKw);
                let p: AgtAkumaPrimeModel;
                let res: AgtAkumaPrimeKwVM[] = [];
                const vm = this.items.find((v) => v.agtId === agtId)!;
                primes.forEach((pl) => {
                    p = pl;
                    res.push(new AgtAkumaPrimeKwVM(vm.agt, p));
                })
                res = res.filter((r) => r.hasData);
                return res;
            },
            {
                cacheResult: true,
            }
        );
    }

    @action
    async loadAkumaPrimeHistoryLast52Weeks(ids: number | number[]): Promise<
        {
            kw: string;
            kwRaw: number;
            einordnungTotal: number;
            kundenkontaktKunden: number;
            kundenkontaktquote: number;
        }[]
    > {
        const isAggregated = Array.isArray(ids);
        const cacheKey = isAggregated
            ? `AgtAkumaPrimeUiStore.loadAkumaPrimeHistoryLast52Weeks_${ids.sort().join('_')}`
            : `AgtAkumaPrimeUiStore.loadAkumaPrimeHistoryLast52Weeks_${ids}`;

        return this.withCache(
            cacheKey,
            async () => {
                // Ensure base data is loaded
                await this.load();

                // Calculate current KW (YYYYWW format)
                const now = moment();
                const currentYear = now.year();
                const currentWeek = now.isoWeek();
                const currentKw = currentYear * 100 + currentWeek;

                // Filter out future weeks
                const pastKws = this.kws.filter(kw => kw <= currentKw);

                // Get last 52 weeks of available past data
                const last52Kws = pastKws.slice(-52);

                // For aggregated view, load all agencies' data
                if (isAggregated) {
                    const agtIds = ids as number[];

                    // Load data for each week and aggregate
                    const result = await Promise.all(
                        last52Kws.map(async (kw) => {
                            // Load all agencies' data for this week
                            const allAgencyData = await this.agtAkumaPrimeStore.aggregateByKw(kw, kw);

                            let totalEinordnungTotal = 0;
                            let totalKundenkontaktKunden = 0;

                            agtIds.forEach((agtId) => {
                                const agtData = allAgencyData.find((m) => m.vtnrId === agtId);
                                if (agtData) {
                                    totalEinordnungTotal += agtData.einordnungTotal || 0;
                                    // Calculate kundenkontaktKunden
                                    totalKundenkontaktKunden +=
                                        (agtData.einordnungKranken || 0) +
                                        (agtData.einordnungLeben || 0) +
                                        (agtData.einordnungSach || 0) +
                                        (agtData.einordnungSonstige || 0) +
                                        (agtData.einordnungFonds || 0);
                                }
                            });

                            return {
                                kw: formatKw(kw),
                                kwRaw: kw,
                                einordnungTotal: totalEinordnungTotal,
                                kundenkontaktKunden: totalKundenkontaktKunden,
                                kundenkontaktquote: totalEinordnungTotal > 0 ? (totalKundenkontaktKunden / totalEinordnungTotal) * 100 : 0,
                            };
                        })
                    );

                    return result;
                } else {
                    // Single agency view
                    const agtId = ids as number;

                    const result = await Promise.all(
                        last52Kws.map(async (kw) => {
                            const models = await this.agtAkumaPrimeStore.aggregateByKw(kw, kw);
                            const agtData = models.find((m) => m.vtnrId === agtId);

                            const kundenkontaktKunden = agtData
                                ? (agtData.einordnungKranken || 0) +
                                  (agtData.einordnungLeben || 0) +
                                  (agtData.einordnungSach || 0) +
                                  (agtData.einordnungSonstige || 0) +
                                  (agtData.einordnungFonds || 0)
                                : 0;

                            const einordnungTotal = agtData?.einordnungTotal || 0;

                            return {
                                kw: formatKw(kw),
                                kwRaw: kw,
                                einordnungTotal,
                                kundenkontaktKunden,
                                kundenkontaktquote: einordnungTotal > 0 ? (kundenkontaktKunden / einordnungTotal) * 100 : 0,
                            };
                        })
                    );

                    return result;
                }
            },
            {
                cacheResult: true,
            },
        );
    }
}
